import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ListOrdered, Languages, CalendarHeart, Settings, Archive, LogOut } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAdmin = () => useContext(AuthCtx);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/admin/auth/me").then((r) => setUser(r.data)).catch(async () => {
      try { await api.post("/admin/auth/refresh"); setUser((await api.get("/admin/auth/me")).data); } catch { setUser(false); }
    });
  }, []);
  return <AuthCtx.Provider value={{ user, setUser }}>{children}</AuthCtx.Provider>;
}

export function AdminLogin() {
  const { user, setUser } = useAdmin();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  if (user) return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { setUser((await api.post("/admin/auth/login", f)).data); navigate("/admin"); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl border bg-white p-8 shadow-sm">
        <h1 className="font-display text-3xl">Team sign in</h1>
        <label className="block"><span className="label">Email</span><input type="email" required className="field" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-login-email" /></label>
        <label className="block"><span className="label">Password</span><input type="password" required className="field" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-login-password" /></label>
        {err && <p className="text-sm text-red-700" role="alert" data-testid="admin-login-error">{err}</p>}
        <button className="btn-plum w-full" data-testid="admin-login-submit">Sign in</button>
      </form>
    </main>
  );
}

const TABS = [
  ["/admin", "Overview", LayoutDashboard, "overview"],
  ["/admin/orders", "Orders", ListOrdered, "orders"],
  ["/admin/languages", "Languages", Languages, "languages"],
  ["/admin/occasions", "Occasions", CalendarHeart, "occasions"],
  ["/admin/settings", "Settings", Settings, "settings"],
  ["/admin/records", "Records", Archive, "records"],
];

export function AdminLayout() {
  const { user, setUser } = useAdmin();
  if (user === null) return <div className="min-h-screen bg-slate-50" />;
  if (user === false) return <Navigate to="/admin/login" replace />;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 lg:flex" data-testid="admin-layout">
      <aside className="bg-slate-900 text-slate-200 lg:min-h-screen lg:w-60">
        <div className="px-5 py-5 font-display text-xl text-white">StoryMelody <span className="text-xs text-slate-400">admin</span></div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col" aria-label="Admin">
          {TABS.map(([to, label, Icon, id]) => (
            <NavLink key={to} to={to} end={to === "/admin"} data-testid={`admin-nav-${id}-tab`}
              className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm ${isActive ? "bg-slate-700 text-white" : "hover:bg-slate-800"}`}>
              <Icon className="h-4 w-4" aria-hidden />{label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden px-5 py-4 text-xs text-slate-400 lg:block">
          {user.email}<br />Role: {user.role}
          <button className="mt-3 flex items-center gap-1 text-slate-300 hover:text-white" data-testid="admin-logout"
            onClick={async () => { await api.post("/admin/auth/logout"); setUser(false); }}><LogOut className="h-3 w-3" />Sign out</button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-5 sm:p-8"><Outlet /></main>
    </div>
  );
}

export const Badge = ({ children, tone = "slate", testid }) => {
  const tones = { slate: "bg-slate-100 text-slate-700", green: "bg-green-100 text-green-800", amber: "bg-amber-100 text-amber-800", red: "bg-red-100 text-red-800", violet: "bg-violet-100 text-violet-800" };
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`} data-testid={testid}>{children}</span>;
};

export const toneFor = (s) => ({ completed: "green", delivered: "green", verified: "green", passed: "green", refunded: "violet", pending: "amber",
  quality_review: "violet", needs_attention: "red", failed: "red", unsupported: "red", cancelled: "slate", generating: "amber", queued: "amber" }[s] || "slate");

export const Card = ({ title, children, actions, testid }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    {(title || actions) && <div className="mb-4 flex items-center justify-between gap-3"><h2 className="font-semibold">{title}</h2>{actions}</div>}
    {children}
  </section>
);
